import { PageBlueprint } from '@backstage/frontend-plugin-api';
import { Navigate, useParams } from 'react-router-dom';

/**
 * The portal does not have people pages, and these two routes are how it stops
 * pretending otherwise.
 *
 * People are the WIKI's to describe. What the catalog needs from a person or a
 * team is a REFERENCE — `spec.owner` — not a destination, and the destination
 * it had was actively bad: the custom entity overview replaces the stock
 * content for every kind, so `user:default/dan.knight` rendered a readiness
 * scorecard docking a human being for having no owning team, no source code
 * linked and no documentation published, above tabs for TechDocs and
 * Dependencies. A group page said the same about a team, and — because the
 * overview is generic — listed neither its members nor anything it owns. There
 * was no version of that page worth reaching.
 *
 * So: an owner ref goes to the catalog filtered by that owner, which is the
 * question the ref was standing in for. Nothing here removes the entities —
 * Group remains the ownership vocabulary, and the relation on the owned entity
 * is untouched. This is only about where a click lands.
 *
 * These are deliberately routes rather than a change to the one link that
 * points at them: the URLs are already in bookmarks and in the address bar's
 * history, and a group ref rendered by any stock Backstage card resolves to
 * this path without asking us. Catching the path catches all of them.
 *
 * Path precedence: the catalog plugin owns `/catalog/*` and routes
 * `:namespace/:kind/:name` beneath it. React Router ranks a route by its
 * segments, and a literal `group` outscores both a `:kind` parameter and a
 * splat, so these win without the catalog plugin needing to know they exist.
 */
const OwnerRedirect = () => {
  const { name } = useParams();
  return (
    <Navigate replace to={`/browse?owner=${encodeURIComponent(name ?? '')}`} />
  );
};

export const groupPageRedirect = PageBlueprint.make({
  name: 'group-redirect',
  params: {
    path: '/catalog/:namespace/group/:name',
    loader: async () => <OwnerRedirect />,
  },
});

/**
 * A user ref has no useful filtered view to land on — ownership is expressed as
 * a team here, so filtering the catalog by a person returns nothing — and the
 * two User entities that existed were an auth substrate rather than content.
 * The honest destination is the catalog itself.
 */
export const userPageRedirect = PageBlueprint.make({
  name: 'user-redirect',
  params: {
    path: '/catalog/:namespace/user/:name',
    loader: async () => <Navigate replace to="/browse" />,
  },
});
